import { VideoModel } from '@loomhq/createhq/dist/video-player';
import {
  VideoContextProvider,
  useModelId,
  useVideoContext,
} from '@loomhq/createhq/dist/video-player/context';
import { Container } from '@loomhq/lens';
import React, { useMemo } from 'react';

import { VIDEO } from 'constants/ssr';
import { isSlackDesktop } from 'utilities/device';
import { getGraphQLClient } from 'utilities/graphql';

import ErrorLayer from './error-layer';
import FetchVideoData from './hooks/FetchVideoData.gql';
import PasswordScreen from './password/modal';
import PrivacyChangedCard from './privacy-changed-card';
import {
  PasswordContextProvider,
  usePasswordContext,
} from './password/context';
import {
  ClipVideoClip,
  parseGraphQLVideo,
  parseVideo,
  sanitizeClip,
} from './utils';
import { userContext, VideoFromGraphQl } from './utils/model';
import {
  ClipVideoPlayer,
  EmbedVideoPlayer,
  ViewfinderVideoPlayer,
} from './VideoPlayer';

export const VideoPlayer: React.FC<{ videoModel: VideoModel }> = ({
  videoModel,
  children,
}): JSX.Element => {
  const contextValue = React.useMemo(
    () => ({
      needsPassword: Boolean(videoModel.needsPassword),
      password: '',
    }),
    [videoModel.needsPassword]
  );

  return (
    <PasswordContextProvider contextValue={contextValue}>
      <VideoContextProvider video={videoModel} context={userContext}>
        <WithPassword>{children}</WithPassword>
      </VideoContextProvider>
    </PasswordContextProvider>
  );
};

const WithPassword: React.FC = ({ children }): React.ReactElement => {
  const passwordCheck = usePasswordCheck();

  if (!passwordCheck.current) {
    return (
      <Container width="100%" height="100%">
        <PasswordScreen />
      </Container>
    );
  }

  return children as React.ReactElement;
};

// The EmbedPageVideo component should Only be used on the embed page,
// because it depends on the SSR video object
export const EmbedPageContainer: React.FC = ({ children }): JSX.Element => (
  <Container position="fixed" top={0} left={0} bottom={0} right={0}>
    {children}
  </Container>
);

export const EmbedPageVideo = (): JSX.Element => {
  const videoModel = window[VIDEO];
  const video = parseVideo(videoModel);

  return (
    <EmbedPageContainer>
      <VideoPlayer videoModel={video}>
        <ErrorLayer />
        <EmbedVideoPlayer slackPlayer={isSlackDesktop} isActive={true} />
      </VideoPlayer>
    </EmbedPageContainer>
  );
};

type EmbedVideoProps = {
  videoModel: VideoFromGraphQl;
};

// The EmbedVideo component can be used anywhere we want to embed a video,
// and we have a GraphQL Video model
export const EmbedVideo = ({ videoModel }: EmbedVideoProps): JSX.Element => {
  const video = parseGraphQLVideo(videoModel) as VideoModel;

  return (
    <VideoPlayer videoModel={video}>
      <EmbedVideoPlayer />
    </VideoPlayer>
  );
};

// FeedEmbedVideo is just like EmbedVideo, except it's meant to be used as a part of
// a card that provides video name, author and other info. These are excluded from the
// player itself.
export const FeedVideo = ({ videoModel }: EmbedVideoProps): JSX.Element => {
  const video = parseGraphQLVideo(videoModel) as VideoModel;

  return (
    <VideoPlayer videoModel={video}>
      <EmbedVideoPlayer feedPlayer={true} />
    </VideoPlayer>
  );
};

type ViewfinderVideoProps = { videoModel: VideoFromGraphQl; pause?: boolean };

export const ViewfinderVideo = ({
  videoModel,
  pause,
}: ViewfinderVideoProps): JSX.Element => {
  const video = parseGraphQLVideo(videoModel) as VideoModel;

  return (
    <VideoPlayer videoModel={video}>
      <ViewfinderVideoPlayer pause={pause} />
    </VideoPlayer>
  );
};

type ClipVideoProps = { videoModel: VideoFromGraphQl; clip: ClipVideoClip };

// The ClipVideo player is meant for the notifications page. It's a simplified version
// of the player that does not include many elements, and plays a subset of the entire video.
export const ClipVideo = ({
  videoModel,
  clip,
}: ClipVideoProps): JSX.Element | null => {
  const video = parseGraphQLVideo(videoModel) as VideoModel;

  const sanitizedClip = useMemo(() => sanitizeClip(video, clip), [video, clip]);

  if (!video) {
    return null;
  }

  if (video.noAccess) {
    return <PrivacyChangedCard />;
  }

  return (
    <>
      <VideoPlayer videoModel={video}>
        <ClipVideoPlayer clip={sanitizedClip} />
      </VideoPlayer>
    </>
  );
};

function usePasswordCheck() {
  const videoId = useModelId();
  const { setVideo } = useVideoContext();
  const { needsPassword, password } = usePasswordContext();
  const passwordCheck = React.useRef(!needsPassword);

  React.useEffect(() => {
    if (!password || passwordCheck.current) {
      return;
    }

    const gqlClient = getGraphQLClient();

    gqlClient
      .query({
        query: FetchVideoData,
        variables: { id: videoId, password },
      })
      .then(({ data }) => {
        passwordCheck.current = true;
        setVideo(parseGraphQLVideo(data.video) as VideoModel);
      });
  }, [password, passwordCheck, setVideo, videoId]);

  return passwordCheck;
}
